import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  ActionItemDetailDto,
  ActionItemSummaryDto,
  EditActionItemDto,
  FetchError,
  queryKeys,
  useAccessToken,
} from "src/http";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  actionItemGuid: string;
  description: string;
  planGuid: string;
};

const useEditActionItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: EditActionItemDto = {
      description: variables.description,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemRequest };
};

const useEditActionItem = () => {
  const { editActionItemRequest } = useEditActionItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemRequest,
    onMutate: async (variables) => {
      const cachedSummaryData = await optimisticallyUpdateQueryData<ActionItemSummaryDto>(
        queryClient,
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        () => ({
          description: variables.description,
        })
      );

      const cachedDetailData = await optimisticallyUpdateQueryData<ActionItemDetailDto>(
        queryClient,
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        () => ({
          description: variables.description,
        })
      );

      return { cachedSummaryData, cachedDetailData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        context?.cachedSummaryData
      );
      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        context?.cachedDetailData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemFeed(variables.actionItemGuid));
    },
  });
};

export { useEditActionItem };
